/**
 * ui/services/sync.ts
 *
 * Cross-device sync for a signed-in player: progress summaries, saved
 * games, replays, and drafts. Each item carries the time it last changed and
 * the newest write wins, on the server and here.
 *
 * A sync pushes what changed on this device since the last push (plus
 * deletions made while signed in) and pulls everything the account got since
 * the last cursor. It runs on sign-in, on start, when the tab comes back or
 * is hidden, every few minutes, shortly after a new replay or a draft edit,
 * and on request. Signed out, nothing happens and nothing is recorded.
 *
 * Only one sync runs at a time per device (a Web Lock across tabs where the
 * browser has them). Deletions are kept under their own storage key, so one
 * recorded while a sync is running isn't lost when the sync saves.
 */
import type { Account, AccountApiPort, SyncChange, SyncKind, SyncResponse } from '../../ports/account.js';
import type { PuzzleProgressPort, PuzzleProgressSummary } from '../../ports/puzzle-progress.js';
import type { PersistedSession, StoragePort } from '../../ports/storage.js';
import type { ReplayRecord, ReplayStorePort } from '../../ports/replays.js';
import type { DraftStorePort } from '../../ports/drafts.js';
import type { PuzzleDraft } from '../../editor/draft.js';
import { ConnectivityError, HttpError } from '../../adapters/http-errors.js';
import { onDeletions } from './sync-deletions.js';
import { browserStorage } from '../../utils/browser-storage.js';

export interface SyncStores {
  progress: PuzzleProgressPort;
  sessions: StoragePort;
  replays: ReplayStorePort;
  drafts: DraftStorePort;
}

/** What sync needs from the account service. */
export interface SyncAccount {
  api: AccountApiPort;
  current(): Account | null;
  token(): string | null;
  onChanged(listener: () => void): () => void;
  /** The server refused `token`; sign out if it's still the one in use. */
  signedOutByServer(token: string): void;
}

/** Items the server wouldn't keep, by reason. */
export interface SyncRejections {
  /** Bigger than the server takes for one item; they stay on this device. */
  tooLarge: number;
  /** The account's storage is full; they're offered again on later syncs. */
  full: number;
}

export type SyncStatus =
  | { state: 'signed-out' }
  | { state: 'idle'; lastSyncAt?: number; rejected?: SyncRejections }
  | { state: 'syncing'; lastSyncAt?: number }
  | { state: 'error'; message: string; offline: boolean; lastSyncAt?: number };

/** What a sync brought in from other devices, by kind. */
export type SyncApplied = Map<SyncKind, Set<string>>;

interface SyncState {
  cursor: number;
  /** Newest change time pushed, per kind. */
  pushed: Record<SyncKind, number>;
  /**
   * Items whose current version is already on the server (pulled, or pushed
   * with a time in the future), by `kind:id` → change time, so they aren't
   * pushed again. Trimmed as the watermark passes them.
   */
  known: Record<string, number>;
  /** Items the server refused for space, offered again next time (`kind:id`). */
  retry: string[];
  lastSyncAt?: number;
}

interface Tombstone {
  kind: SyncKind;
  id: string;
  deletedAt: number;
}

export const SYNC_STATE_PREFIX = 'nono_sync:';
export const SYNC_TOMBSTONES_PREFIX = 'nono_sync_tombstones:';
/** The last account this device synced with (a different one doesn't get this device's data). */
export const SYNC_LAST_ACCOUNT_KEY = 'nono_sync_last_account';
export const SYNC_INTERVAL_MS = 5 * 60_000;
/** A sync soon after local changes, batched. */
export const SYNC_SOON_MS = 10_000;
const MAX_BATCH_CHANGES = 100;
/** Keep each request well under the server's 2 MB body limit. */
const MAX_BATCH_BYTES = 1_500_000;
/** The server's limit for one item; bigger ones (a huge replay) stay on this device. */
export const SYNC_MAX_ITEM_BYTES = 1024 * 1024;
/** How far ahead of now a change time can be before it's treated as a clock error. */
const CLOCK_AHEAD_MS = 5 * 60_000;
const MAX_PAGES = 1000;

type StateStorage = Pick<Storage, 'getItem' | 'setItem'>;

function emptyState(): SyncState {
  return { cursor: 0, pushed: { progress: 0, session: 0, replay: 0, draft: 0 }, known: {}, retry: [] };
}

function time(iso: string | undefined): number {
  const parsed = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

const encoder = new TextEncoder();

function byteLength(value: unknown): number {
  return encoder.encode(JSON.stringify(value)).length;
}

const keyOf = (kind: SyncKind, id: string) => `${kind}:${id}`;

/** Local items by id with their change time, loaded once per sync and kept current as changes apply. */
interface LocalIndex {
  progress: Map<string, PuzzleProgressSummary>;
  sessions: Map<string, number>;
  replays: Map<string, number>;
  drafts: Map<string, PuzzleDraft>;
}

export class SyncService {
  private _status: SyncStatus = { state: 'signed-out' };
  private readonly _listeners = new Set<(status: SyncStatus) => void>();
  private _running: Promise<void> | null = null;
  private _again = false;
  private _soonTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly _stopDeletions: () => void;

  constructor(
    private readonly _account: SyncAccount,
    private readonly _stores: SyncStores,
    private readonly _options: {
      storage?: StateStorage;
      now?: () => number;
      /** Called with what a sync brought in from other devices. */
      onApplied?: (applied: SyncApplied) => void;
      /** Where deletions are announced (tests use their own). */
      subscribeDeletions?: typeof onDeletions;
      /** Runs `task` holding a device-wide lock (tests and browsers without Web Locks run it directly). */
      withLock?: (name: string, task: () => Promise<void>) => Promise<void>;
    } = {},
  ) {
    this._status = _account.current() ? { state: 'idle' } : { state: 'signed-out' };
    // Record deletions whenever signed in, even before start().
    this._stopDeletions = (_options.subscribeDeletions ?? onDeletions)((kind, ids) => this._recordDeletions(kind, ids));
  }

  /** Stops listening for deletions (tests). */
  destroy(): void {
    this._stopDeletions();
    if (this._soonTimer) clearTimeout(this._soonTimer);
  }

  status(): SyncStatus {
    return this._status;
  }

  onStatus(listener: (status: SyncStatus) => void): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  /**
   * Runs sync on its own schedule: now, on sign-in, when the tab comes
   * back or is hidden, and every few minutes. Returns a stop function.
   */
  start(): () => void {
    const stops: Array<() => void> = [];
    stops.push(this._account.onChanged(() => {
      const account = this._account.current();
      this._setStatus(account ? { state: 'idle', lastSyncAt: this._loadState(account.id).lastSyncAt } : { state: 'signed-out' });
      if (account) void this.syncNow();
    }));
    // Back to the tab: catch up. Leaving it: push what changed here.
    const onVisible = () => {
      if (globalThis.document?.visibilityState === 'visible') this.syncSoon(1000);
      else void this.syncNow();
    };
    globalThis.document?.addEventListener('visibilitychange', onVisible);
    stops.push(() => globalThis.document?.removeEventListener('visibilitychange', onVisible));
    const interval = setInterval(() => void this.syncNow(), SYNC_INTERVAL_MS);
    stops.push(() => clearInterval(interval));
    void this.syncNow();
    return () => {
      for (const stop of stops) stop();
      if (this._soonTimer) clearTimeout(this._soonTimer);
    };
  }

  /** Syncs after a short wait, batching bursts of local changes. */
  syncSoon(delayMs = SYNC_SOON_MS): void {
    if (!this._account.current()) return;
    if (this._soonTimer) clearTimeout(this._soonTimer);
    this._soonTimer = setTimeout(() => {
      this._soonTimer = null;
      void this.syncNow();
    }, delayMs);
  }

  /** Syncs now; while one runs, another follows it. Resolves when done (errors go to the status). */
  syncNow(): Promise<void> {
    if (this._running) {
      this._again = true;
      return this._running;
    }
    this._running = (async () => {
      try {
        do {
          this._again = false;
          const accountId = this._account.current()?.id;
          if (!accountId) {
            this._setStatus({ state: 'signed-out' });
            break;
          }
          await this._withLock(`nono-sync:${accountId}`, () => this._syncOnce());
        } while (this._again);
      } finally {
        this._running = null;
      }
    })();
    return this._running;
  }

  private async _withLock(name: string, task: () => Promise<void>): Promise<void> {
    if (this._options.withLock) return this._options.withLock(name, task);
    const locks = (globalThis.navigator as Navigator | undefined)?.locks;
    if (locks) await locks.request(name, task);
    else await task();
  }

  private async _syncOnce(): Promise<void> {
    const account = this._account.current();
    const token = this._account.token();
    if (!account || !token) {
      this._setStatus({ state: 'signed-out' });
      return;
    }
    // Read inside the lock: another tab may have just synced.
    const state = this._loadState(account.id);
    this._setStatus({ state: 'syncing', lastSyncAt: state.lastSyncAt });
    const applied: SyncApplied = new Map();
    const rejected: SyncRejections = { tooLarge: 0, full: 0 };
    const now = this._now();
    try {
      this._firstSyncHere(account.id, state, now);
      const local = await this._loadLocal();
      const tombstones = this._loadTombstones(account.id);
      const outgoing = await this._localChanges(state, local, tombstones);
      const { batches, skipped } = batch(outgoing);
      rejected.tooLarge += skipped;
      const retry = new Set(state.retry);
      let more = true;
      for (let page = 0; page < MAX_PAGES && (batches.length > 0 || more); page++) {
        const changes = batches.shift() ?? [];
        const firstPull = state.cursor === 0;
        let response: SyncResponse;
        try {
          response = await this._account.api.sync(token, { since: state.cursor, changes });
        } catch (err) {
          // The request was over the server's size limit: those items stay here.
          if (err instanceof HttpError && err.status === 413 && changes.length > 0) {
            rejected.tooLarge += changes.length;
            this._markPushed(state, changes, retry, now);
            this._saveState(account.id, state);
            continue;
          }
          throw err;
        }
        if (response.reset) {
          // The server was restored from a backup: pull everything again.
          state.cursor = 0;
          more = true;
        }
        const refused = new Set<string>();
        for (const item of response.rejected) {
          const key = keyOf(item.kind, item.id);
          refused.add(key);
          if (item.reason === 'account storage full') {
            rejected.full++;
            retry.add(key);
          } else {
            rejected.tooLarge++;
          }
        }
        this._markPushed(state, changes.filter((change) => !refused.has(keyOf(change.kind, change.id))), retry, now);
        this._forgetTombstones(account.id, changes.filter((change) => change.deleted));
        for (const change of response.changes) {
          // An account's old deletions say nothing about what this device
          // has; on the first pull here they're not applied.
          if (change.deleted && firstPull) continue;
          if (await this._apply(change, local, now)) {
            if (!applied.has(change.kind)) applied.set(change.kind, new Set());
            applied.get(change.kind)!.add(change.id);
          }
          if (change.updatedAt > state.pushed[change.kind]) state.known[keyOf(change.kind, change.id)] = change.updatedAt;
        }
        if (!response.reset) {
          state.cursor = response.cursor;
          more = response.more;
        }
        state.retry = [...retry];
        this._saveState(account.id, state);
      }
      this._finish(state, now);
      this._saveState(account.id, state);
      this._rememberAccount(account.id);
      if (applied.size > 0) this._options.onApplied?.(applied);
      const anyRejected = rejected.tooLarge > 0 || rejected.full > 0;
      this._setStatus({ state: 'idle', lastSyncAt: state.lastSyncAt, ...(anyRejected ? { rejected } : {}) });
    } catch (err) {
      if (applied.size > 0) this._options.onApplied?.(applied);
      if (err instanceof HttpError && err.status === 401) {
        this._account.signedOutByServer(token);
        if (!this._account.current()) this._setStatus({ state: 'signed-out' });
        return;
      }
      this._setStatus({
        state: 'error',
        message: err instanceof Error ? err.message : String(err),
        offline: err instanceof ConnectivityError,
        lastSyncAt: state.lastSyncAt,
      });
    }
  }

  /**
   * The first sync with an account on this device. If this device's data was
   * last synced with a different account, it stays out of this one (a shared
   * device mustn't pour one player's data into another's account): only the
   * account's items come in, and later changes go up. A device that never
   * synced (a guest's data) adds its data to the account.
   */
  private _firstSyncHere(accountId: string, state: SyncState, now: number): void {
    if (state.lastSyncAt !== undefined || state.cursor !== 0) return;
    const last = this._readRaw(SYNC_LAST_ACCOUNT_KEY);
    if (last && last !== accountId) {
      for (const kind of Object.keys(state.pushed) as SyncKind[]) state.pushed[kind] = now;
    }
  }

  private _rememberAccount(accountId: string): void {
    this._writeRaw(SYNC_LAST_ACCOUNT_KEY, accountId);
  }

  private _markPushed(state: SyncState, changes: SyncChange[], retry: Set<string>, now: number): void {
    for (const change of changes) {
      const key = keyOf(change.kind, change.id);
      retry.delete(key);
      if (change.deleted) continue;
      if (change.updatedAt > now + CLOCK_AHEAD_MS) {
        // Stamped by a clock running fast: don't let it drag the watermark into the future.
        state.known[key] = change.updatedAt;
      } else {
        state.pushed[change.kind] = Math.max(state.pushed[change.kind], change.updatedAt);
      }
    }
  }

  private _finish(state: SyncState, now: number): void {
    for (const kind of Object.keys(state.pushed) as SyncKind[]) {
      // A watermark left in the future (a clock since corrected) would hide every new change.
      if (state.pushed[kind] > now + CLOCK_AHEAD_MS) state.pushed[kind] = now;
    }
    for (const [key, updatedAt] of Object.entries(state.known)) {
      const kind = key.slice(0, key.indexOf(':')) as SyncKind;
      if (updatedAt <= state.pushed[kind]) delete state.known[key];
    }
    state.lastSyncAt = now;
  }

  private async _loadLocal(): Promise<LocalIndex> {
    const [progress, sessions, replays, drafts] = await Promise.all([
      this._stores.progress.listProgress(),
      this._stores.sessions.listSessions(),
      this._stores.replays.listReplays(),
      this._stores.drafts.listDrafts(),
    ]);
    return {
      progress: new Map(progress.map((item) => [item.puzzleId, item])),
      sessions: new Map(sessions.map((item) => [item.puzzleId, time(item.savedAt)])),
      replays: new Map(replays.map((item) => [item.id, item.completedAt])),
      drafts: new Map(drafts.map((item) => [item.id, item])),
    };
  }

  private async _localChanges(state: SyncState, local: LocalIndex, tombstones: Tombstone[]): Promise<SyncChange[]> {
    const changes: SyncChange[] = [];
    const retry = new Set(state.retry);
    const wanted = (kind: SyncKind, id: string, updatedAt: number) => {
      const key = keyOf(kind, id);
      if (state.known[key] === updatedAt) return false;
      return updatedAt > state.pushed[kind] || retry.has(key);
    };
    for (const item of local.progress.values()) {
      const updatedAt = time(item.savedAt);
      if (wanted('progress', item.puzzleId, updatedAt)) changes.push({ kind: 'progress', id: item.puzzleId, updatedAt, data: item });
    }
    for (const [id, updatedAt] of local.sessions) {
      if (!wanted('session', id, updatedAt)) continue;
      const session = await this._stores.sessions.loadSession(id);
      if (session) changes.push({ kind: 'session', id, updatedAt, data: session });
    }
    for (const [id, updatedAt] of local.replays) {
      if (!wanted('replay', id, updatedAt)) continue;
      const record = await this._stores.replays.getReplay(id);
      if (record) changes.push({ kind: 'replay', id, updatedAt, data: record });
    }
    for (const item of local.drafts.values()) {
      if (wanted('draft', item.id, item.updatedAt)) changes.push({ kind: 'draft', id: item.id, updatedAt: item.updatedAt, data: item });
    }
    for (const tombstone of tombstones) {
      changes.push({ kind: tombstone.kind, id: tombstone.id, updatedAt: tombstone.deletedAt, deleted: true });
    }
    // Oldest first, so an interrupted sync still moves the watermarks forward.
    return changes.sort((a, b) => a.updatedAt - b.updatedAt);
  }

  /** Applies another device's change when it's newer than what's here. Returns whether anything changed. */
  private async _apply(change: SyncChange, local: LocalIndex, now: number): Promise<boolean> {
    const { id, updatedAt } = change;
    switch (change.kind) {
      case 'progress': {
        const existing = local.progress.get(id);
        const existingAt = existing ? time(existing.savedAt) : -1;
        if (change.deleted) {
          if (!existing || existingAt > updatedAt) return false;
          await this._stores.progress.deleteProgress(id);
          local.progress.delete(id);
          return true;
        }
        const data = change.data;
        if (!isObject(data) || data.puzzleId !== id || typeof data.completed !== 'boolean' || existingAt >= updatedAt) return false;
        let summary = data as unknown as PuzzleProgressSummary;
        if (existing?.completed && !summary.completed) {
          // Solved stays solved: keep the mark, and send it back up (a newer time).
          summary = { ...summary, completed: true, savedAt: new Date(Math.max(now, updatedAt + 1)).toISOString() };
        }
        await this._stores.progress.saveProgress(summary);
        local.progress.set(id, summary);
        return true;
      }
      case 'session': {
        const existingAt = local.sessions.get(id) ?? -1;
        if (change.deleted) {
          if (existingAt < 0 || existingAt > updatedAt) return false;
          await this._stores.sessions.deleteSession(id);
          local.sessions.delete(id);
          return true;
        }
        const data = change.data;
        if (!isObject(data) || data.puzzleId !== id || !Array.isArray(data.board) || existingAt >= updatedAt) return false;
        const session = data as unknown as PersistedSession;
        await this._stores.sessions.saveSession(session);
        local.sessions.set(id, time(session.savedAt));
        return true;
      }
      case 'replay': {
        const existing = local.replays.has(id);
        if (change.deleted) {
          if (!existing) return false;
          await this._stores.replays.deleteReplay(id);
          local.replays.delete(id);
          return true;
        }
        const data = change.data;
        if (existing || !isObject(data) || data.id !== id || !Array.isArray(data.moveLog)) return false;
        const record = data as unknown as ReplayRecord;
        await this._stores.replays.saveReplay(record);
        local.replays.set(id, record.completedAt);
        return true;
      }
      case 'draft': {
        const existing = local.drafts.get(id);
        const existingAt = existing ? existing.updatedAt : -1;
        if (change.deleted) {
          if (!existing || existingAt > updatedAt) return false;
          await this._stores.drafts.deleteDraft(id);
          local.drafts.delete(id);
          return true;
        }
        const data = change.data;
        if (!isObject(data) || data.id !== id || !Array.isArray(data.cells) || existingAt >= updatedAt) return false;
        const draft = data as unknown as PuzzleDraft;
        await this._stores.drafts.saveDraft(draft);
        local.drafts.set(id, draft);
        return true;
      }
    }
  }

  private _recordDeletions(kind: SyncKind, ids: string[]): void {
    const account = this._account.current();
    if (!account) return;
    const deletedAt = this._now();
    const removed = new Set(ids);
    const tombstones = this._loadTombstones(account.id).filter((t) => !(t.kind === kind && removed.has(t.id)));
    for (const id of ids) tombstones.push({ kind, id, deletedAt });
    this._writeRaw(SYNC_TOMBSTONES_PREFIX + account.id, JSON.stringify(tombstones));
    this.syncSoon();
  }

  /** Drops tombstones the server now has (re-reading, so ones recorded meanwhile stay). */
  private _forgetTombstones(accountId: string, sent: SyncChange[]): void {
    if (sent.length === 0) return;
    const sentAt = new Map(sent.map((change) => [keyOf(change.kind, change.id), change.updatedAt]));
    const left = this._loadTombstones(accountId).filter((t) => {
      const at = sentAt.get(keyOf(t.kind, t.id));
      return at === undefined || t.deletedAt > at;
    });
    this._writeRaw(SYNC_TOMBSTONES_PREFIX + accountId, JSON.stringify(left));
  }

  private _loadTombstones(accountId: string): Tombstone[] {
    try {
      const parsed: unknown = JSON.parse(this._readRaw(SYNC_TOMBSTONES_PREFIX + accountId) ?? '[]');
      return Array.isArray(parsed) ? parsed.filter((t): t is Tombstone => isObject(t) && typeof t.id === 'string') : [];
    } catch {
      return [];
    }
  }

  private _now(): number {
    return (this._options.now ?? Date.now)();
  }

  private get _storage(): StateStorage | null {
    return this._options.storage ?? browserStorage();
  }

  private _readRaw(key: string): string | null {
    try {
      return this._storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  }

  private _writeRaw(key: string, value: string): void {
    try {
      this._storage?.setItem(key, value);
    } catch {
      // Storage full or unavailable: the next sync pushes again (the server ignores repeats).
    }
  }

  private _loadState(accountId: string): SyncState {
    try {
      const parsed = JSON.parse(this._readRaw(SYNC_STATE_PREFIX + accountId) ?? 'null') as Partial<SyncState> | null;
      if (!parsed) return emptyState();
      const base = emptyState();
      return {
        cursor: typeof parsed.cursor === 'number' ? parsed.cursor : 0,
        pushed: { ...base.pushed, ...(isObject(parsed.pushed) ? parsed.pushed : {}) },
        known: isObject(parsed.known) ? parsed.known as Record<string, number> : {},
        retry: Array.isArray(parsed.retry) ? parsed.retry.filter((key): key is string => typeof key === 'string') : [],
        ...(typeof parsed.lastSyncAt === 'number' ? { lastSyncAt: parsed.lastSyncAt } : {}),
      };
    } catch {
      return emptyState();
    }
  }

  private _saveState(accountId: string, state: SyncState): void {
    this._writeRaw(SYNC_STATE_PREFIX + accountId, JSON.stringify(state));
  }

  private _setStatus(status: SyncStatus): void {
    this._status = status;
    for (const listener of this._listeners) listener(status);
  }
}

/** Splits changes into requests the server accepts (count and bytes), leaving out items it would refuse. */
function batch(changes: SyncChange[]): { batches: SyncChange[][]; skipped: number } {
  const batches: SyncChange[][] = [];
  let current: SyncChange[] = [];
  let bytes = 0;
  let skipped = 0;
  for (const change of changes) {
    const size = byteLength(change);
    if (size > SYNC_MAX_ITEM_BYTES) {
      skipped++;
      continue;
    }
    if (current.length > 0 && (current.length >= MAX_BATCH_CHANGES || bytes + size > MAX_BATCH_BYTES)) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(change);
    bytes += size;
  }
  if (current.length > 0) batches.push(current);
  return { batches, skipped };
}
